import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { PackageSearch, Summary } from "lucide-react";

import { EmptyState } from "@/components/EmptyState";
import { QuickFilterCards, QuickFilterCardsSkeleton } from "@/components/QuickFilterCards";

import { OFFER_QUICK_FILTERS, type OfferQuickFilterId } from "./quick-filters";
import { useOfferStatusCounts } from "./useOfferStatusCounts";

const OFFERS_PATH = "/representative/offer";

interface RootState {
  userDetail?: { data?: { organization?: number } };
  listTraders?: { data?: { results?: { id: number; name: string }[] } };
}

export const RepresentativeHome = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { counts, isLoading } = useOfferStatusCounts();
  const buyerName = useSelector(
    (state: RootState) =>
      state.listTraders?.data?.results?.find(
        ({ id }) => id === state.userDetail?.data?.organization
      )?.name
  );
  const [selected, setSelected] = useState<OfferQuickFilterId | null>(null);

  const visibleFilters = useMemo(
    () => OFFER_QUICK_FILTERS.filter(filter => (counts[filter.id] ?? 0) > 0),
    [counts]
  );

  const handleChange = (id: OfferQuickFilterId | null) => {
    setSelected(id);
    if (id) {
      history.push(OFFERS_PATH, { status: id });
    }
  };

  return (
    <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
      <div className="flex flex-col gap-1">
        <h1 className="flex items-center gap-2 font-semibold text-2xl">
          <Summary className="size-7 shrink-0 text-blue-600" aria-hidden="true" />
          {buyerName
            ? t("home.representative.title", { buyer_name: buyerName })
            : t("header.overview")}
        </h1>
        <p className="text-muted-foreground">{t("home.representative.subtitle")}</p>
      </div>

      <section className="flex flex-col gap-6">
        {isLoading ? (
          <QuickFilterCardsSkeleton filters={OFFER_QUICK_FILTERS} />
        ) : visibleFilters.length ? (
          <QuickFilterCards
            counts={counts}
            filters={visibleFilters}
            ariaLabel={t("home.representative.offers.ariaLabel")}
            value={selected}
            onChange={handleChange}
          />
        ) : (
          <EmptyState
            icon={PackageSearch}
            title={t("home.representative.offers.emptyTitle")}
            description={t("home.representative.offers.emptyDescription")}
          />
        )}
      </section>
    </main>
  );
};
